import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaRocket, FaCheckCircle } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';

const CTABoxes: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleNavigate = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="get-started" className="py-20 px-6 relative" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at top, rgba(99, 102, 241, 0.3), transparent 70%), radial-gradient(ellipse at bottom, rgba(236, 72, 153, 0.3), transparent 70%)', filter: 'blur(80px)' }}></div>
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t.cta.title}
          </h2>
          <p
            className="text-xl"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t.cta.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Box 1: The Proof Project */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -10 }}
            className="glass-strong relative p-8 rounded-3xl transition-all duration-300 flex flex-col h-full"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)'
            }}
          >
            {/* Badge */}
            <div
              className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-full font-bold text-sm shadow-lg"
              style={{
                backgroundColor: 'var(--color-success)',
                color: 'white'
              }}
            >
              {t.cta.poc.badge}
            </div>

            <div className="text-center mt-4 flex flex-col flex-grow">
              {React.createElement(FaRocket, {
                className: "w-16 h-16 mx-auto mb-6",
                style: { color: 'var(--color-primary)' }
              })}

              <h3
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {t.cta.poc.title}
              </h3>

              <p
                className="text-5xl font-bold mb-6"
                style={{ color: 'var(--color-success)' }}
              >
                {t.cta.poc.price}
              </p>

              <p
                className="text-lg leading-relaxed mb-8"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {t.cta.poc.description}
              </p>

              <ul className="text-left space-y-4 mb-8 flex-grow">
                {t.cta.poc.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    {React.createElement(FaCheckCircle, {
                      className: "flex-shrink-0 mt-1",
                      style: { color: 'var(--color-success)' }
                    })}
                    <span style={{ color: 'var(--color-text-primary)' }}>{feature}</span>
                  </li>
                ))}
              </ul>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNavigate('/contact#poc')}
                className="w-full py-4 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  minHeight: '64px'
                }}
              >
                {t.cta.poc.button}
              </motion.button>
            </div>
          </motion.div>

          {/* Box 2: The Guaranteed Growth Project */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -10 }}
            className="glass-strong relative p-8 rounded-3xl transition-all duration-300 flex flex-col h-full"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
              color: 'white',
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.5)'
            }}
          >
            {/* Badge */}
            <div
              className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-full font-bold text-sm shadow-lg"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: 'white'
              }}
            >
              {t.cta.guaranteed.badge}
            </div>

            <div className="text-center mt-4 flex flex-col flex-grow">
              {React.createElement(FaCheckCircle, { className: "w-16 h-16 mx-auto mb-6" })}

              <h3 className="text-3xl font-bold mb-2">
                {t.cta.guaranteed.title}
              </h3>

              <p className="text-5xl font-bold mb-6">
                {t.cta.guaranteed.price}
              </p>

              <p className="text-lg leading-relaxed mb-8 opacity-95">
                {t.cta.guaranteed.description}
              </p>

              <ul className="text-left space-y-4 mb-8 flex-grow">
                {t.cta.guaranteed.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    {React.createElement(FaCheckCircle, { className: "flex-shrink-0 mt-1" })}
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNavigate('/contact#guaranteed')}
                className="w-full py-4 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                style={{
                  backgroundColor: 'white',
                  color: 'var(--color-primary)',
                  minHeight: '64px'
                }}
              >
                {t.cta.guaranteed.button}
              </motion.button>
            </div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12 text-lg"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {t.cta.question} <button onClick={() => handleNavigate('/contact#general')} className="underline font-semibold" style={{ color: 'var(--color-primary)' }}>{t.cta.talk}</button>
        </motion.p>
      </div>
    </section>
  );
};

export default CTABoxes;
